import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes, getItemsToCreditNote } from 'src/actions/invoice.actions'
import {
  selectInvoiceCreditNote,
  selectInvoiceItemsCreditNote,
} from 'src/selectors/invoice.selector'
import { selectCompanyCountry } from 'src/selectors/company.selector'
import { Country } from 'src/enums/countries'
import { loadingSelector } from 'src/selectors/loading.selector'
import { sumWithDynamicScale } from 'src/utils/numberSumUtil'
import { showAlert } from 'src/actions/alert.actions'
import { handlerError, hasErrors } from 'src/selectors/error.selector'

interface ICreditNoteStatus {
  generalAmount: number
  disabledInventory: boolean
  disabledPartial: boolean
  isSellNullify: boolean
  used: number
  init: boolean
}

const defaultCreditNoteStatus: ICreditNoteStatus = {
  generalAmount: 0,
  disabledInventory: false,
  disabledPartial: false,
  isSellNullify: false,
  used: 0,
  init: false,
}

export const useCreditNoteStatus = (
  init: boolean,
  sellId: number,
  partial: boolean,
  returnInventory: boolean,
  callBack: (returnInventory: boolean, partial: boolean) => void,
  onClose: (update: boolean) => void,
): ICreditNoteStatus => {
  const dispatch = useDispatch()

  const creditNoteFromSell: ICreditNoteData = useSelector(selectInvoiceCreditNote)
  const country = useSelector(selectCompanyCountry)
  const items: ICreditNoteItem[] = useSelector(selectInvoiceItemsCreditNote)

  const hasErrorGet = useSelector(state =>
    hasErrors([actionTypes.GET_INVOICE_ITEMS_TO_CREDIT_NOTE])(state),
  )
  const loadingGet: boolean = useSelector(state =>
    loadingSelector([actionTypes.GET_INVOICE_ITEMS_TO_CREDIT_NOTE])(state),
  )

  const isSV: boolean = country.id === Country.SV
  const amountUsed: number = creditNoteFromSell.creditNoteAmount
  const isSellNullify: boolean = creditNoteFromSell.sellNullify

  const [flag, setFlag] = useState<boolean>(false)

  const [response, setResponse] = useState<ICreditNoteStatus>(defaultCreditNoteStatus)

  useEffect(() => {
    if (init) {
      dispatch(getItemsToCreditNote(sellId))
    } else setResponse(defaultCreditNoteStatus)
  }, [init])

  useEffect(() => {
    if (loadingGet) setFlag(true)
    else if (flag) {
      setFlag(false)
      if (hasErrorGet) {
        dispatch(
          showAlert({
            ...handlerError(hasErrorGet.message),
            onConfirm: () => onClose(false),
          }),
        )
        return
      }

      if (amountUsed > 0) {
        const partialAmount = !isSellNullify
        callBack(false, partialAmount)
      }

      // GENERAL AMOUNT
      const values = items.map(it => it.total).concat(-amountUsed)
      const generalAmount = sumWithDynamicScale(values)

      setResponse({
        generalAmount,
        disabledInventory: false,
        disabledPartial: false,
        used: amountUsed,
        isSellNullify,
        init: true,
      })
    }
  }, [loadingGet])

  const disabledInventory = response.init
    ? response.isSellNullify || (isSV ? response.used > 0 || partial : false)
    : false
  const disabledPartial = response.init
    ? isSV
      ? response.used > 0 || returnInventory
      : false
    : false

  return { ...response, disabledInventory, disabledPartial }
}
